import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FileText, Download, CheckCircle, CreditCard, AlertTriangle, Building2,
} from "lucide-react";
import useRazorpay, { PLATFORM_MERCHANT } from "../../hooks/useRazorpay";
import { getBaseUrl } from "../../utils/getBaseUrl";
import CommonToaster from "../Common/CommonToaster";
import "./public-invoice-payment.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
  });
}

const STATUS_CONFIG = {
  sent: { label: "Sent", className: "pip-badge-info" },
  viewed: { label: "Viewed", className: "pip-badge-primary" },
  pending_payment: { label: "Pending Payment", className: "pip-badge-warning" },
  paid: { label: "Paid", className: "pip-badge-success" },
  overdue: { label: "Overdue", className: "pip-badge-danger" },
  cancelled: { label: "Cancelled", className: "pip-badge-secondary" },
};

const SERVICE_TYPE_LABELS = {
  custom_development: "Custom Development",
  integration: "Integration",
  support: "Support",
  amc: "AMC",
  training: "Training",
  revenue_share: "Revenue Share",
  others: "Others",
};

// Plain axios client — no auth interceptors. The token in the URL is the
// only credential needed for these public endpoints.
const publicApi = axios.create({
  baseURL: getBaseUrl(),
  headers: { "Content-Type": "application/json" },
});

const PublicInvoicePaymentPage = () => {
  const { token } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [institute, setInstitute] = useState(null);
  const [payable, setPayable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const fetchInvoice = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const { data } = await publicApi.get(`/public/service-invoices/${token}`);
      setInvoice(data.invoice);
      setInstitute(data.institute);
      setPayable(!!data.payable);
    } catch (err) {
      setLoadError(err.response?.data?.message || "Failed to load invoice.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchInvoice(); }, [fetchInvoice]);

  const handleDownloadPdf = async () => {
    try {
      const response = await publicApi.get(`/public/service-invoices/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch {
      setToast({ show: true, message: "Failed to generate PDF.", type: "error" });
    }
  };

  const handlePay = async () => {
    try {
      const { data } = await publicApi.post(`/public/service-invoices/${token}/pay/create-order`);
      await openCheckout(
        {
          razorpayKeyId: data.razorpayKeyId,
          order: data.order,
          merchantName: PLATFORM_MERCHANT,
          student: {
            name: data.institute?.name || "",
            email: data.institute?.email || "",
            mobile: data.institute?.phone || "",
          },
          description: `Invoice ${data.invoice?.invoiceNumber || ""}`,
        },
        async (response) => {
          try {
            await publicApi.post(`/public/service-invoices/${token}/pay/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setToast({ show: true, message: "Payment successful!", type: "success" });
            fetchInvoice();
          } catch (err) {
            setToast({ show: true, message: err.response?.data?.message || "Payment verification failed.", type: "error" });
          }
        },
        (err) => {
          if (err?.reason !== "Payment cancelled by user.") {
            setToast({ show: true, message: err?.reason || "Payment failed.", type: "error" });
          }
        }
      );
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to initiate payment.", type: "error" });
    }
  };

  if (loading) {
    return (
      <div className="pip-wrapper">
        <div className="pip-card pip-loading">Loading invoice…</div>
      </div>
    );
  }

  if (loadError || !invoice) {
    return (
      <div className="pip-wrapper">
        <div className="pip-card pip-error">
          <AlertTriangle size={28} />
          <h2>Invoice not available</h2>
          <p>{loadError || "Invoice not found or link expired."}</p>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[invoice.status] || {};
  const billingFrom = invoice.billingFrom || {};
  const billingTo = invoice.billingTo || {};
  const hasTax = invoice.taxRate > 0 && invoice.taxAmount > 0;
  const isIntra = (invoice.cgstRate || 0) > 0;
  const isInter = (invoice.igstRate || 0) > 0;

  return (
    <div className="pip-wrapper">
      <div className="pip-card">
        {/* Header */}
        <div className="pip-header">
          <div>
            <div className="pip-eyebrow">Payment Invoice</div>
            <h1>{invoice.invoiceNumber}</h1>
            <div className="pip-subtitle">
              {billingFrom.companyName || "Service Provider"}
              {institute?.name ? ` → ${institute.name}` : ""}
            </div>
          </div>
          <div className={`pip-badge ${sc.className || ""}`}>{sc.label || invoice.status}</div>
        </div>

        {/* Status messages */}
        {invoice.status === "paid" && (
          <div className="pip-alert pip-alert-success">
            <CheckCircle size={18} />
            <div>
              <strong>Payment received.</strong> This invoice has been marked as paid
              {invoice.payment?.paidAt ? ` on ${formatDate(invoice.payment.paidAt)}` : ""}.
              {invoice.payment?.transactionId && (
                <div className="pip-muted">Transaction ID: {invoice.payment.transactionId}</div>
              )}
            </div>
          </div>
        )}
        {invoice.status === "overdue" && (
          <div className="pip-alert pip-alert-danger">
            <AlertTriangle size={18} />
            <div>This invoice is overdue. Please complete the payment at the earliest.</div>
          </div>
        )}
        {invoice.status === "cancelled" && (
          <div className="pip-alert pip-alert-danger">
            <AlertTriangle size={18} />
            <div>This invoice has been cancelled and cannot be paid.</div>
          </div>
        )}

        {/* Amount + Pay CTA */}
        <div className="pip-amount-row">
          <div>
            <div className="pip-muted">Total Amount Due</div>
            <div className="pip-amount">{formatCurrency(invoice.totalAmount)}</div>
            <div className="pip-muted">Due Date: {formatDate(invoice.dueDate)}</div>
          </div>
          <div className="pip-actions">
            <button type="button" className="pip-btn pip-btn-outline" onClick={handleDownloadPdf}>
              <Download size={16} /> Download PDF
            </button>
            {payable && invoice.status !== "paid" && (
              <button type="button" className="pip-btn pip-btn-primary" onClick={handlePay} disabled={rzpLoading}>
                <CreditCard size={16} /> {rzpLoading ? "Processing…" : `Pay ${formatCurrency(invoice.totalAmount)}`}
              </button>
            )}
          </div>
        </div>

        {/* Billing parties */}
        <div className="pip-grid">
          {billingFrom.companyName && (
            <div className="pip-block">
              <h3><Building2 size={14} /> From</h3>
              <div className="pip-block-body">
                <div><strong>{billingFrom.companyName}</strong></div>
                {billingFrom.address && <div>{billingFrom.address}</div>}
                {(billingFrom.city || billingFrom.state) && (
                  <div>
                    {[billingFrom.city, billingFrom.state, billingFrom.pincode].filter(Boolean).join(", ")}
                  </div>
                )}
                {billingFrom.gstin && <div>GSTIN: {billingFrom.gstin}</div>}
                {billingFrom.email && <div>{billingFrom.email}</div>}
                {billingFrom.phone && <div>{billingFrom.phone}</div>}
              </div>
            </div>
          )}

          <div className="pip-block">
            <h3><Building2 size={14} /> Bill To</h3>
            <div className="pip-block-body">
              <div><strong>{billingTo.name || institute?.name || "—"}</strong></div>
              {billingTo.address && <div>{billingTo.address}</div>}
              {billingTo.gstin && <div>GSTIN: {billingTo.gstin}</div>}
              {billingTo.email && <div>{billingTo.email}</div>}
              {billingTo.phone && <div>{billingTo.phone}</div>}
            </div>
          </div>

          <div className="pip-block">
            <h3><FileText size={14} /> Invoice</h3>
            <div className="pip-block-body">
              <div>Number: <strong>{invoice.invoiceNumber}</strong></div>
              <div>Service: {SERVICE_TYPE_LABELS[invoice.serviceType] || invoice.serviceType}</div>
              {invoice.sacCode && <div>SAC: {invoice.sacCode}</div>}
              {invoice.placeOfSupply && <div>Place of Supply: {invoice.placeOfSupply}</div>}
              <div>Issue Date: {formatDate(invoice.sentAt || invoice.createdAt)}</div>
              <div>Due Date: {formatDate(invoice.dueDate)}</div>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="pip-block pip-line-items">
          <h3>Line Items</h3>
          <div className="pip-table-wrap">
            <table className="pip-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Description</th>
                  <th className="pip-num">Qty</th>
                  <th className="pip-num">Unit Price</th>
                  <th className="pip-num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {invoice.lineItems?.map((item, idx) => (
                  <tr key={item._id || idx}>
                    <td>{idx + 1}</td>
                    <td>{item.description}</td>
                    <td className="pip-num">{item.quantity}</td>
                    <td className="pip-num">{formatCurrency(item.unitPrice)}</td>
                    <td className="pip-num">{formatCurrency(item.amount)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={4} className="pip-total-label">Subtotal</td>
                  <td className="pip-num">{formatCurrency(invoice.subtotal || invoice.totalAmount)}</td>
                </tr>
                {isIntra && (
                  <>
                    <tr>
                      <td colSpan={4} className="pip-total-label">CGST @ {invoice.cgstRate}%</td>
                      <td className="pip-num">{formatCurrency(invoice.cgstAmount)}</td>
                    </tr>
                    <tr>
                      <td colSpan={4} className="pip-total-label">SGST @ {invoice.sgstRate}%</td>
                      <td className="pip-num">{formatCurrency(invoice.sgstAmount)}</td>
                    </tr>
                  </>
                )}
                {isInter && (
                  <tr>
                    <td colSpan={4} className="pip-total-label">IGST @ {invoice.igstRate}%</td>
                    <td className="pip-num">{formatCurrency(invoice.igstAmount)}</td>
                  </tr>
                )}
                {!isIntra && !isInter && hasTax && (
                  <tr>
                    <td colSpan={4} className="pip-total-label">GST @ {invoice.taxRate}%</td>
                    <td className="pip-num">{formatCurrency(invoice.taxAmount)}</td>
                  </tr>
                )}
                {invoice.roundedOff !== undefined && invoice.roundedOff !== 0 && (
                  <tr>
                    <td colSpan={4} className="pip-total-label">Rounded Off</td>
                    <td className="pip-num">{invoice.roundedOff > 0 ? "+" : ""}{invoice.roundedOff.toFixed(2)}</td>
                  </tr>
                )}
                <tr className="pip-grand-total">
                  <td colSpan={4} className="pip-total-label">Total Invoice Amount</td>
                  <td className="pip-num">{formatCurrency(invoice.totalAmount)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {invoice.notes && (
          <div className="pip-block">
            <h3>Notes</h3>
            <p className="pip-notes">{invoice.notes}</p>
          </div>
        )}

        <div className="pip-footer">
          Secure payment processed by Razorpay. This payment link is private — do not share publicly.
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default PublicInvoicePaymentPage;
